import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import parseValidators, {
  TValidatorSpec,
  TValidatorHash,
  TValidatorSpecParam,
} from './core/parseValidators';
import validationProcessor from './core/validationProcessor';
import FormContext, { TErrors } from './FormContext';

export interface IUseValidationProps<D = any> {
  /** The current data of the form */
  data?: D;
  /** The data first provided for the form */
  initialData?: D;
  /** Errors associated with the initial data */
  initialErrors?: TErrors;
  /** The validators to run on the form */
  validators?: TValidatorSpecParam<D>;
  /* The function run on the form changes. Provided as any to allow flexibility */
  onChange?: (name: string, value: any, onBlur?: boolean) => any;
  /* The name of the component being validated */
  name?: string;
}

export interface IRunningValidator<D = any> {
  /** The function run when the validator is finished */
  callback?: (validationResult: D) => unknown;
  /** Errors of the child components */
  childErrors?: TErrors;
  /** States weather the children have no errors */
  childrenValid?: boolean;
  /** The name of the current running validator */
  name?: string;
  /** Promise used during the validation process */
  promise?: any;
  /** Should the validation fail silently */
  silent?: boolean;
}

export function generateErrors<D = any>(
  validators: TValidatorSpecParam<D>,
  data: D,
  name?: string
) {
  const validatorsForData = name
    ? { [name]: (validators as TValidatorSpec<D>)?.[name] }
    : { ...validators };
  const parsedValidators = parseValidators.getValidators<D>(validatorsForData as TValidatorHash<D>);
  return validationProcessor.runValidators(data, parsedValidators).then((value: any) =>
    Object.keys(parsedValidators).reduce(
      (memo, key) => ({
        ...memo,
        [key]: (value && value[key]) || [],
      }),
      {}
    )
  );
}

const getIsValid = (errors: { [s: string]: unknown } | ArrayLike<unknown>) =>
  !Object.values(errors).filter(
    (value) => Array.isArray(value) && value.filter(Boolean).length && typeof value[0] !== 'object'
  ).length;

// TODO: Make this better
export interface IValidationState {
  data: any;
  errors: any;
  isValid: boolean;
}

export type TFormValue = ReturnType<typeof useValidation>[1];

/**
 * In here so that the identity of the initialData and initialError objects don't
 * change when the useValidation hook reruns
 */
const emptyObject = {};

/**
 * A custom React hook that manages state for a validated form or form section.
 *
 * The return value is an array containing the FormContext Provider component, the value to pass to
 * the Provider, and a copy of the value for ease of destructuring when those values are needed.
 */
function useValidation<D = any>(
  {
    data: dataProp,
    initialData = emptyObject,
    initialErrors = emptyObject,
    name: nameProp = '',
    onChange: onChangeProp,
    validators: validatorsProp,
  }: IUseValidationProps = {} as IUseValidationProps
) {
  const [state, setState] = useState<IValidationState>({
    data: dataProp || initialData,
    errors: initialErrors,
    isValid: true,
  });
  const [runningValidator, setRunningValidator] = useState<IRunningValidator | null>(null);
  const skipNotify = useRef(false);

  const registeredValidators = useRef<TValidatorHash | { [key: string]: any }>({});
  const childSelfValidators = useRef<{
    [key: string]: (
      key?: string,
      silent?: boolean,
      data?: D
    ) => Promise<IValidationState> | IValidationState;
  }>({});

  const resetData = useCallback(
    () =>
      setState((prevState) => ({
        ...prevState,
        data: dataProp || initialData,
      })),
    [dataProp, initialData]
  );
  const resetErrors = useCallback(
    () =>
      setState((prevState) => ({
        ...prevState,
        errors: initialErrors,
      })),
    [initialErrors]
  );

  // When the data option changes, we need to reset our data to it
  useEffect(() => {
    if (dataProp && dataProp !== state.data) {
      skipNotify.current = true;
      resetData();
    }
  }, [dataProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // When our data (primarily) changes, we should notify the caller through the onChange prop
  // This is primarily used to sync FormSections to the root controller as the hook returns the data
  // and causes a rerender when it changes
  useEffect(() => {
    if (skipNotify.current) {
      skipNotify.current = false;
      return;
    }

    if (onChangeProp) {
      onChangeProp(nameProp, state.data);
    }
  }, [nameProp, onChangeProp, state.data]);

  useEffect(() => {
    if (!runningValidator) {
      return undefined;
    }

    let ignore = false;
    let calledBack = false;

    const { callback, childErrors, childrenValid, name, promise, silent } = runningValidator;

    promise.then((nextErrors: TErrors) => {
      if (ignore) {
        return;
      }

      setState((prevState) => {
        const mergedErrors = {
          ...prevState.errors,
          ...(name ? { [name]: undefined } : {}),
          ...nextErrors,
          ...childErrors,
        };

        const nextState = {
          errors: mergedErrors,
          isValid: !!(childrenValid && getIsValid(mergedErrors)),
        };

        callback?.(nextState);
        calledBack = true;

        if (silent) {
          return prevState;
        }

        setRunningValidator(null);

        return {
          ...prevState,
          ...nextState,
        };
      });
    });

    return () => {
      ignore = true;

      if (callback && !calledBack) {
        callback({ isValid: false });
      }
    };
  }, [runningValidator]);

  // Takes field-by-field validators and links them back to this so we can validate the whole form
  const unregisterValidators = useCallback((name: string) => {
    delete registeredValidators.current[name as keyof typeof registerValidators];

    setState((prevState) => {
      const nextErrors = { ...prevState.errors };
      delete nextErrors[name];
      return {
        ...prevState,
        errors: nextErrors,
      };
    });
  }, []);
  const registerValidators = useCallback(
    (name: string, validators: TValidatorSpec[] | { [key: string]: any }) => {
      registeredValidators.current[name] = validators;

      /** @deprecated */
      return () => unregisterValidators(name);
    },
    [unregisterValidators]
  );

  // Used by FormSection and ListFormSection to link validation back to the root controller,
  // can also be used for complex inputs
  const unregisterSelfValidatingChild = useCallback((name: string) => {
    delete childSelfValidators.current[name];
  }, []);
  const registerSelfValidatingChild = useCallback(
    (
      name: string,
      fn: (key?: string, silent?: boolean, data?: D) => Promise<IValidationState> | IValidationState
    ) => {
      childSelfValidators.current[name] = fn;
      return () => unregisterSelfValidatingChild(name);
    },
    [unregisterSelfValidatingChild]
  );

  // Used by inputs and sections to update our state and call field-by-field validation
  const onChange = useCallback(
    (name: string, value: unknown, validate?: boolean) => {
      setState((prevState) => {
        const validators = { ...validatorsProp, ...registeredValidators.current };
        const nextData = {
          ...prevState.data,
          [name]: value,
        };

        if (validate) {
          generateErrors(validators, nextData, name).then((errors) => {
            setState((morePrevState) =>
              morePrevState.data[name] === value
                ? {
                    ...morePrevState,
                    errors: {
                      ...morePrevState.errors,
                      [name]: undefined,
                      ...errors,
                    },
                  }
                : morePrevState
            );
          });
        }

        return {
          ...prevState,
          data: nextData,
        };
      });
    },
    [validatorsProp]
  );

  // Validates the entire field or just the section/field at `name`
  const validate = useCallback(
    (name?: string, silent?: boolean) =>
      new Promise<IValidationState>((resolve) => {
        const order = Object.keys(childSelfValidators.current);

        // If we have a section or complex input that validates itself, we need to collect those values
        // before running our own validation
        const selfValidators = name
          ? childSelfValidators.current[name]
            ? [childSelfValidators.current[name](undefined, silent || false)]
            : []
          : order.map((key) => childSelfValidators.current[key](undefined, silent || false));
        Promise.all(selfValidators).then((orderedResults) => {
          const {
            data: childData,
            errors: childErrors,
            isValid: childrenValid,
          } = orderedResults.reduce<IValidationState>(
            ({ data, errors, isValid }, value, index) => {
              const key = name ?? order[index];

              // Converts the array results of promise.all back into a matching format to our state
              return {
                data: {
                  ...data,
                  [key]: value.data,
                },
                errors: {
                  ...errors,
                  [key]: value.errors,
                },
                isValid: isValid && value.isValid,
              } as IValidationState;
            },
            {
              data: {},
              errors: {},
              isValid: true,
            }
          );

          // Updates our state by merging the child results in and running our validations
          setState((prevState) => {
            const validators = { ...validatorsProp, ...registeredValidators.current };

            setRunningValidator({
              callback: (validationResult: D) =>
                resolve({
                  ...prevState,
                  data: {
                    ...prevState.data,
                    ...childData,
                  },
                  ...validationResult,
                }),
              childErrors,
              childrenValid,
              name,
              promise: generateErrors(validators, prevState.data, name),
              silent,
            });

            return {
              ...prevState,
              data: {
                ...prevState.data,
                ...childData,
              },
            };
          });
        });
      }),
    [validatorsProp]
  );

  // If we don't memoize this, it will change on each call, causing everything to rerender
  return useMemo(() => {
    const value = {
      data: state.data,
      errors: state.errors,
      onChange,
      registerSelfValidatingChild,
      registerValidators,
      resetData,
      resetErrors,
      unregisterSelfValidatingChild,
      unregisterValidators,
      validate,
    };

    return [FormContext.Provider, value, value] as [
      typeof FormContext.Provider,
      typeof value,
      typeof value,
    ];
  }, [
    onChange,
    validate,
    state.data,
    state.errors,
    resetData,
    resetErrors,
    // These don't ever change but lint complains about them
    unregisterSelfValidatingChild,
    unregisterValidators,
    registerSelfValidatingChild,
    registerValidators,
  ]);
}

export default useValidation;
